// ImpactReportingPage.js
import React from 'react';
import { Box, Typography, Container, Breadcrumbs, Link as MuiLink, Grid, List, ListItem, ListItemText } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';

const ImpactReportingPage = () => {
  const breadcrumbs = [
    { name: 'Home', path: '/' },
    { name: 'Sustainability', path: '/sustainability' },
    { name: 'Impact Reporting', path: '/sustainability/impact-reporting' },
  ];

  const reportSections = [
    {
      title: 'Our Annual Sustainability Report',
      description: 'Each year, we publish a comprehensive report detailing our environmental, social, and governance (ESG) performance. This report highlights our progress, challenges, and future goals.',
      details: [
        'Key performance indicators (KPIs) and metrics.',
        'Case studies of successful recycling initiatives.',
        'Financial contributions to sustainable projects.',
      ],
      link: '#annual-report-link', // Placeholder for a link to an actual report
      linkText: 'Download Latest Report (PDF)',
    },
    {
      title: 'Transparency in Data',
      description: 'We believe in full transparency. Our reporting includes verifiable data on material volumes, carbon footprint reductions, and other key environmental benefits achieved through our platform.',
      details: [
        'Methodologies for data collection and analysis.',
        'Third-party verification of reported data.',
        'Interactive dashboards (future feature) for real-time insights.',
      ],
    },
    {
      title: 'Goals and Progress',
      description: 'We set ambitious sustainability goals and regularly report on our progress towards achieving them. This includes targets for waste reduction, energy efficiency, and community engagement.',
      details: [
        'Short-term and long-term sustainability objectives.',
        'Updates on current initiatives and their outcomes.',
        'Stakeholder feedback and continuous improvement plans.',
      ],
    },
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', p: 2 }}>
      <Container sx={{ py: { xs: 4, md: 6 }, bgcolor: 'background.paper', borderRadius: 2, boxShadow: 1 }}>
        {/* Breadcrumbs */}
        <Box sx={{ mb: { xs: 2, md: 4 }, px: { xs: 2, md: 0 } }}>
          <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
            {breadcrumbs.map((crumb, index) => (
              crumb.path ? (
                <MuiLink component={RouterLink} to={crumb.path} key={index} underline="hover" color="text.secondary" variant="body2">
                  {crumb.name}
                </MuiLink>
              ) : (
                <Typography key={index} color="text.primary" variant="body2">{crumb.name}</Typography>
              )
            ))}
          </Breadcrumbs>
        </Box>

        <Typography variant="h3" align="center" fontWeight={700} sx={{ mb: { xs: 4, md: 6 }, color: 'text.primary' }}>
          Impact Reporting
        </Typography>

        <Typography variant="body1" align="center" sx={{ mb: { xs: 4, md: 6 }, maxWidth: '800px', mx: 'auto', color: 'text.secondary' }}>
          Transparency and accountability are key to our sustainability efforts. Our impact reporting provides a clear and detailed overview of how our operations contribute to a more sustainable future.
        </Typography>

        <Box sx={{ mt: 6 }}>
          {reportSections.map((section, index) => (
            <Box
              key={index}
              sx={{
                mb: 6,
                p: { xs: 3, md: 5 },
                borderRadius: 4,
                background: 'background.paper',
                boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                border: '1px solid',
                borderColor: 'grey.200',
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: '0 16px 32px rgba(0,0,0,0.12)',
                },
              }}
            >
              <Typography variant="h4" fontWeight={700} sx={{ mb: 2, color: 'primary.dark' }}>
                {section.title}
              </Typography>
              <Typography variant="body1" sx={{ mb: 3, color: 'text.primary' }}>
                {section.description}
              </Typography>

              <Grid container spacing={4}>
                <Grid item xs={12}>
                  <Box>
                    <Typography variant="h6" fontWeight={600} sx={{ color: 'secondary.main', mb: 1.5 }}>
                      What You'll Find:
                    </Typography>
                    <List dense disablePadding>
                      {section.details.map((detail, detailIndex) => (
                        <ListItem key={detailIndex} disablePadding sx={{ py: 0.5 }}>
                          <ListItemText primary={detail} primaryTypographyProps={{ variant: 'body1', color: 'text.secondary' }} />
                        </ListItem>
                      ))}
                    </List>
                    {section.link && (
                      <MuiLink href={section.link} target="_blank" rel="noopener noreferrer" sx={{ mt: 2, display: 'inline-block', fontWeight: 500 }}>
                        {section.linkText} &rarr;
                      </MuiLink>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Box>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export default ImpactReportingPage;
